import { createSlice } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { rootReducer } from '../store'
import { applicationsApi } from './applications-api'
import { applicationsManagerApi } from './applications-manager'

export const applicationsSlice = createSlice({
  name: 'applications',
  initialState: {
    lists: {} as Record<string, Paginated<ApplicationListItem>>,
    adminLists: {} as Record<string, Paginated<ApplicationAdminListItem>>,
    details: {} as Record<string, ApplicationDetail>,
    adminDetails: {} as Record<string, ApplicationDetail>,
    companies: null as Paginated<ExecutorCompanyOption> | null,
    statuses: [] as ChoiceItem[],
    outcomes: [] as ChoiceItem[],
    checkStatuses: [] as ChoiceItem[],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      applicationsApi.endpoints.listApplications.matchFulfilled,
      (state, { payload, meta }) => {
        state.lists[JSON.stringify(meta.arg.originalArgs || {})] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.listAdminApplications.matchFulfilled,
      (state, { payload, meta }) => {
        state.adminLists[JSON.stringify(meta.arg.originalArgs || {})] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.getApplication.matchFulfilled,
      (state, { payload, meta }) => {
        state.details[meta.arg.originalArgs] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.getAdminApplication.matchFulfilled,
      (state, { payload, meta }) => {
        state.adminDetails[meta.arg.originalArgs] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.listExecutorCompanies.matchFulfilled,
      (state, { payload }) => { state.companies = payload },
    )
    builder.addMatcher(
      applicationsApi.endpoints.metaStatuses.matchFulfilled,
      (state, { payload }) => { state.statuses = payload },
    )
    builder.addMatcher(
      applicationsApi.endpoints.metaOutcomes.matchFulfilled,
      (state, { payload }) => { state.outcomes = payload },
    )
    builder.addMatcher(
      applicationsApi.endpoints.metaCheckItemStatuses.matchFulfilled,
      (state, { payload }) => { state.checkStatuses = payload },
    )
    builder.addMatcher(
      applicationsApi.endpoints.createApplication.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.createApplicationFromOrder.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.confirmCheckItemTnvedChanges.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
        if (state.adminDetails[payload.public_id]) {
          state.adminDetails[payload.public_id] = payload
        }
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.postCheckItemComment.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.answerClarification.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.requestExecutorTransfer.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.requestContactExchange.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
        if (state.adminDetails[payload.public_id]) {
          state.adminDetails[payload.public_id] = payload
        }
      },
    )
    builder.addMatcher(
      applicationsApi.endpoints.declineProposal.matchFulfilled,
      (state, { payload }) => {
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.assignManager.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.assignExecutor.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.updateCheckItem.matchFulfilled,
      (state, { payload }) => {
        const application = payload && typeof payload === 'object' && 'application' in payload
          ? (payload as ApplicationCheckItemResponse).application
          : payload
        if (application?.public_id) {
          state.adminDetails[application.public_id] = application
          if (state.details[application.public_id]) {
            state.details[application.public_id] = application
          }
        }
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.uploadCheckItemDocument.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.deleteCheckItemDocument.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.createClarification.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.postAdminCheckItemComment.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
        state.details[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.completePreparation.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.saveProposalDraft.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.publishProposal.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.revealContacts.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.saveClientContacts.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.saveExecutorContacts.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
    builder.addMatcher(
      applicationsManagerApi.endpoints.updateOutcome.matchFulfilled,
      (state, { payload }) => {
        state.adminDetails[payload.public_id] = payload
      },
    )
  },
})

export const useApplicationsList = ({ params, managerView = false }: ApplicationsListSelectorArgs) => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => {
    const lists = managerView ? applications.adminLists : applications.lists
    return lists[JSON.stringify(params || {})]
  })
}

export const useApplication = ({ publicId, managerView }: ApplicationSelectorArgs) => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => {
    const details = managerView ? applications.adminDetails : applications.details
    return details[publicId]
  })
}

export const useApplicationStatuses = () => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => applications.statuses)
}

export const useApplicationOutcomes = () => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => applications.outcomes)
}

export const useApplicationCheckStatuses = () => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => applications.checkStatuses)
}

export const useApplicationExecutorCompanies = () => {
  return useSelector(({ applications }: ReturnType<typeof rootReducer>) => applications.companies)
}
